
"use client";

import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageItem } from "./message-item";
import type { PubSubMessage } from "@/types";
import { Loader2, Inbox, WifiOff } from "lucide-react";
import { useEffect, useRef } from "react";

type MessageListProps = {
  messages: PubSubMessage[];
  filterKeyword: string;
  isConnected: boolean; // Renamed from isSubscribed for clarity here, means connection to a subscription
  isLoadingConnection: boolean; // True if trying to subscribe/unsubscribe or initial message fetch
};

export function MessageList({ messages, filterKeyword, isConnected, isLoadingConnection }: MessageListProps) {
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const filteredMessages = messages.filter((msg) => {
    if (!filterKeyword) return true;
    const keyword = filterKeyword.toLowerCase();
    
    // Check ID
    if (msg.id.toLowerCase().includes(keyword)) return true;

    // Check attributes
    if (msg.attributes) {
      for (const key in msg.attributes) {
        if (String(msg.attributes[key]).toLowerCase().includes(keyword)) return true;
        if (key.toLowerCase().includes(keyword)) return true;
      }
    }
    
    // Check data payload
    const dataString = typeof msg.data === 'string' ? msg.data : JSON.stringify(msg.data);
    if (dataString.toLowerCase().includes(keyword)) return true;
    
    return false;
  });

  useEffect(() => {
    if (scrollAreaRef.current) {
      // Attempt to find the viewport element within ScrollArea
      const viewport = scrollAreaRef.current.querySelector('div[data-radix-scroll-area-viewport]');
      if (viewport) {
         // Only auto-scroll if the user is already near the bottom
        const isScrolledToBottom = viewport.scrollHeight - viewport.scrollTop <= viewport.clientHeight + 20; // 20px buffer
        if (isScrolledToBottom) {
            viewport.scrollTop = viewport.scrollHeight;
        }
      }
    }
  }, [messages]); // Trigger effect when messages array changes


  if (isLoadingConnection && messages.length === 0 && !isConnected) { // Show general loading if initially connecting/subscribing
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-lg">Connecting and fetching initial messages...</p>
      </div>
    );
  }

  if (!isConnected && !isLoadingConnection) { // explicitly not connected and not loading
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <WifiOff className="h-16 w-16 mb-4 text-destructive" />
        <p className="text-xl font-medium">Not Connected</p>
        <p>Please enter Project ID, fetch topics, and connect.</p>
      </div>
    );
  }
  
  if (isConnected && messages.length === 0 && isLoadingConnection) { // Connected but loading initial messages
     return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-lg">Fetching initial messages...</p>
      </div>
    );
  }


  if (isConnected && messages.length === 0 && !isLoadingConnection) { // Connected, not loading, but no messages yet
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Inbox className="h-16 w-16 mb-4" />
        <p className="text-xl font-medium">Waiting for messages...</p>
        <p>New messages from your subscription will appear here.</p>
      </div>
    );
  }
  
  if (filteredMessages.length === 0 && messages.length > 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Inbox className="h-16 w-16 mb-4" />
        <p className="text-xl font-medium">No messages match your filter</p>
        <p>Try adjusting your filter keyword or clearing the filter.</p>
      </div>
    );
  }


  return (
    <ScrollArea className="h-full flex-grow" ref={scrollAreaRef}>
      <div className="p-1">
        {filteredMessages.map((msg) => (
          <MessageItem key={msg.id} message={msg} />
        ))}
        {isConnected && isLoadingConnection && messages.length > 0 && ( // Show subtle loading at the bottom if polling
            <div className="flex items-center justify-center py-4 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin text-primary mr-2" />
                <span>Checking for new messages...</span>
            </div>
        )}
      </div>
    </ScrollArea>
  );
}
